import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  Form,
  FormButton,
  Input,
  string,
  useNotifications
} from '@ijoin/components';
import {
  usePatchAdminLoginMfaVerify,
  usePostAdminLoginMfaGenerate
} from '@ijoin/hooks';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type EnableMfaDialogProps = {
  loginId: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const EnableMfaDialog = (props: EnableMfaDialogProps) => {
  const notifications = useNotifications();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');

  const methods = useForm({
    defaultValues: { code: '' },
    mode: 'all',
    resolver: zodResolver(
      z.object({ code: string({ required: true }).length(6) })
    )
  });

  const generateMfa = usePostAdminLoginMfaGenerate({
    mutation: {
      onSuccess: data => setQrCodeDataUrl(data.qrCodeDataUrl)
    }
  });

  const verifyMfa = usePatchAdminLoginMfaVerify({
    mutation: {
      onSuccess: () => {
        props.onClose();
        props.onSuccess();
        notifications.success('MFA enabled');
      }
    }
  });

  useEffect(() => {
    generateMfa.mutate({ loginId: props.loginId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data: { code: string }) => {
    verifyMfa.mutate({ data: { code: data.code }, loginId: props.loginId });
  };

  return (
    <Dialog data-testid='enableMfaDialog' isOpen onClose={props.onClose}>
      <Form
        methods={methods}
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>Enable MFA</DialogTitle>
        <DialogContent>
          <Stack alignItems='center' spacing={2} sx={{ mt: 1 }}>
            <Typography variant='body2'>
              Scan the QR code with an authenticator app, then enter the 6-digit
              code to verify.
            </Typography>
            {qrCodeDataUrl && (
              <img
                alt='QR Code'
                src={qrCodeDataUrl}
                style={{ height: 200, width: 200 }}
              />
            )}
            <Input
              data-1p-ignore
              data-testid='mfaVerifyCode'
              format='string'
              fullWidth
              label='Verification Code'
              name='code'
              size='small'
            />
            {generateMfa.isError && (
              <Alert severity='error'>Failed to generate QR code</Alert>
            )}
            {verifyMfa.isError && (
              <Alert severity='error'>Invalid verification code</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelEnableMfa'
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            data-testid='confirmEnableMfa'
            triggerValidation
            variant='contained'>
            Verify & Enable
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
